import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { FiMessageSquare, FiMail } from 'react-icons/fi';
import ConfirmCard from '../../components/common/ConfirmCard';
import { listProposals, bulkDecline, confirmAction, declineAction } from '../../api/proposalsApi';
import { isAiProposalsEnabled } from '../../helpers/agentFeature';

const CustomerPopup = lazy(() => import('../../components/Profile/CustomerPopup'));

// "waited" since a message arrived — the MESSAGE's age, not the proposal's.
// Prefer a backend-computed business-hours label; fall back to wall-clock.
function waitedFrom(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const mins = Math.max(1, Math.round((Date.now() - then) / 60000));
  if (mins < 60) return `waited ${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `waited ${hrs}h`;
  return `waited ${Math.round(hrs / 24)}d`;
}
const CHANNEL_ICON = { sms: FiMessageSquare, email: FiMail };

/* AI Proposals inbox — the face of the ledger's PENDING proposals (chat + the
   proactive Watcher, whose proposals have no conversation to live in). Reuses
   the shared ConfirmCard (3rd consumer, after receipts + the chat panel).
   Confirm/decline reuse the same /agent/confirm//decline/ the chat panel wired;
   shadow proposals render with Confirm disabled — the pilot only records. */

const toolLabel = (tool) => String(tool || 'action')
  .replace(/^agent\./, '')
  .replace(/[_.]/g, ' ')
  .replace(/\b\w/g, (c) => c.toUpperCase());

// Agent proposal surfaces: watcher / chat / mobile / voice. Chips render
// dynamically off counts.by_surface, so an unmapped surface still shows (raw key).
const SURFACE_LABEL = { watcher: 'Watcher', chat: 'Chat', mobile: 'Mobile', voice: 'Voice' };
const SURFACE_TONE = { watcher: 'warn', chat: 'ok', mobile: 'new', voice: 'match' };

function ageFrom(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

const S = {
  page: { padding: '22px 28px', maxWidth: 900, margin: '0 auto' },
  h1: { fontSize: 20, fontWeight: 700, color: 'var(--text-main)', margin: '0 0 4px' },
  sub: { fontSize: 13, color: 'var(--text-muted)', margin: '0 0 18px' },
  filters: { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 },
  filterGroupLabel: { fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', color: 'var(--text-muted)', marginRight: 2 },
  chip: (active) => ({
    padding: '4px 11px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
    border: '1px solid var(--border)',
    background: active ? 'var(--accent)' : 'transparent',
    color: active ? 'var(--accent-ink)' : 'var(--text-main)',
  }),
  bulkBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
    padding: '8px 12px', margin: '10px 0', borderRadius: 10,
    background: 'var(--bg-1)', border: '1px solid var(--border)',
  },
  bulkBtn: {
    padding: '6px 12px', borderRadius: 8, border: '1px solid var(--danger)',
    background: 'transparent', color: 'var(--danger)', fontWeight: 700, fontSize: 12, cursor: 'pointer',
  },
  linkBtn: { background: 'transparent', border: 'none', color: 'var(--accent)', fontWeight: 600, fontSize: 12, cursor: 'pointer', padding: 0 },
  row: { display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 },
  check: { marginTop: 16, width: 16, height: 16, flexShrink: 0, cursor: 'pointer', accentColor: 'var(--accent)' },
  cardWrap: { flex: 1, minWidth: 0 },
  empty: { textAlign: 'center', color: 'var(--text-muted)', fontSize: 14, padding: '48px 20px', lineHeight: 1.6 },
  loadMore: { display: 'block', margin: '16px auto 0', padding: '8px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-main)', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  note: { fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', marginTop: 12 },
  nameLink: { background: 'transparent', border: 'none', padding: 0, color: 'var(--accent)', fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 },
  chanChip: { display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' },
};

// Title node for an inbox-context proposal: the customer name as a link that
// opens CustomerPopup, with a channel glyph. Falls back to the tool label.
function CardTitle({ proposal, onOpenCustomer }) {
  const name = proposal.customer_name;
  const puid = proposal.subject?.party_universal_id || proposal.party_universal_id;
  const Chan = CHANNEL_ICON[proposal.channel];
  if (!name) return <>{String(proposal.tool || 'action').replace(/^agent\./, '').replace(/[_.]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {puid ? (
        <button style={S.nameLink} onClick={() => onOpenCustomer({ universal_id: puid, name })} title={`Open ${name}'s profile`}>
          {name}
        </button>
      ) : (
        <span style={{ fontWeight: 700 }}>{name}</span>
      )}
      {Chan && <span style={S.chanChip}><Chan size={12} /> {proposal.channel}</span>}
    </span>
  );
}

export default function ProposalsPage() {
  const enabled = isAiProposalsEnabled();
  const [proposals, setProposals] = useState([]);
  const [counts, setCounts] = useState({ total: 0, by_rule: {}, by_surface: {} });
  const [next, setNext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [surface, setSurface] = useState(null);
  const [rule, setRule] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [resolved, setResolved] = useState(() => ({})); // action_id -> 'confirmed' | 'rejected'
  const [busy, setBusy] = useState(() => new Set());
  const [profileFor, setProfileFor] = useState(null); // {universal_id, name} to open in CustomerPopup

  const load = useCallback(async (append = false, cursor = null) => {
    setLoading(true); setError(null);
    try {
      const { data } = await listProposals({ status: 'pending', surface, rule, cursor });
      const rows = Array.isArray(data?.proposals) ? data.proposals : [];
      setProposals((prev) => append ? [...prev, ...rows] : rows);
      setCounts(data?.counts || { total: rows.length, by_rule: {}, by_surface: {} });
      setNext(data?.next || null);
    } catch (e) {
      setError(e?.response?.status === 404 ? 'The proposals service isn’t available yet.' : 'Couldn’t load proposals.');
    } finally {
      setLoading(false);
    }
  }, [surface, rule]);

  useEffect(() => { if (enabled) { setSelected(new Set()); load(false, null); } }, [enabled, load]);

  const markBusy = (id, on) => setBusy((s) => { const n = new Set(s); on ? n.add(id) : n.delete(id); return n; });
  // Tell the sidebar badge to re-poll the pending count after an action, so it
  // doesn't sit stale until its 60s tick.
  const notifyBadge = () => { try { window.dispatchEvent(new Event('ai-proposals:changed')); } catch { /* noop */ } };

  const resolveOne = useCallback(async (proposal, kind) => {
    const id = proposal.action_id;
    if (!id || busy.has(id)) return;
    markBusy(id, true);
    try {
      const fn = kind === 'confirmed' ? confirmAction : declineAction;
      await fn(id);
      setResolved((r) => ({ ...r, [id]: kind }));
      setSelected((s) => { const n = new Set(s); n.delete(id); return n; });
      notifyBadge();
    } catch (e) {
      // leave it pending; surface a soft error on the card row
      setResolved((r) => ({ ...r, [id]: undefined }));
      setError(`Couldn’t ${kind === 'confirmed' ? 'confirm' : 'decline'} that proposal (${e?.response?.status || 'network'}).`);
    } finally {
      markBusy(id, false);
    }
  }, [busy]);

  const doBulkDecline = useCallback(async () => {
    const ids = [...selected].filter((id) => !resolved[id]);
    if (!ids.length) return;
    ids.forEach((id) => markBusy(id, true));
    try {
      const { data } = await bulkDecline(ids);
      const failed = new Set((data?.failed || []).map((f) => f.action_id));
      setResolved((r) => {
        const n = { ...r };
        ids.forEach((id) => { if (!failed.has(id)) n[id] = 'rejected'; });
        return n;
      });
      setSelected(new Set());
      notifyBadge();
      if (failed.size) setError(`${failed.size} proposal(s) couldn’t be declined.`);
    } catch (e) {
      setError(`Bulk decline failed (${e?.response?.status || 'network'}).`);
    } finally {
      ids.forEach((id) => markBusy(id, false));
    }
  }, [selected, resolved]);

  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const surfaceKeys = useMemo(() => Object.keys(counts.by_surface || {}), [counts]);
  // Rules are a watcher concept; chat proposals have rule=null (keyed "none" in
  // counts). Drop that pseudo-rule from the chips — the Surface filter already
  // separates chat. Label real rules off the server's rule_label, not the key.
  const ruleLabels = useMemo(() => {
    const m = {};
    proposals.forEach((p) => { if (p.rule && p.rule_label) m[p.rule] = p.rule_label; });
    return m;
  }, [proposals]);
  const ruleKeys = useMemo(
    () => Object.keys(counts.by_rule || {}).filter((k) => k && k !== 'none' && k !== 'null'),
    [counts],
  );
  const selectableCount = useMemo(() => proposals.filter((p) => !resolved[p.action_id]).length, [proposals, resolved]);

  if (!enabled) {
    return (
      <div style={S.page}>
        <h1 style={S.h1}>AI Proposals</h1>
        <p style={S.empty}>The assistant isn’t enabled for this workspace yet.</p>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <h1 style={S.h1}>AI Proposals</h1>
      <p style={S.sub}>
        Actions the assistant and watchers propose — with what each would do. Nothing runs without approval.
      </p>

      <div style={S.filters}>
        <span style={S.filterGroupLabel}>Surface</span>
        <span style={S.chip(!surface)} onClick={() => setSurface(null)} role="button">All</span>
        {surfaceKeys.map((k) => (
          <span key={k} style={S.chip(surface === k)} onClick={() => setSurface(surface === k ? null : k)} role="button">
            {SURFACE_LABEL[k] || k} ({counts.by_surface[k]})
          </span>
        ))}
      </div>
      {ruleKeys.length > 0 && (
        <div style={S.filters}>
          <span style={S.filterGroupLabel}>Rule</span>
          <span style={S.chip(!rule)} onClick={() => setRule(null)} role="button">All</span>
          {ruleKeys.map((k) => (
            <span key={k} style={S.chip(rule === k)} onClick={() => setRule(rule === k ? null : k)} role="button">
              {ruleLabels[k] || k} ({counts.by_rule[k]})
            </span>
          ))}
        </div>
      )}

      {selected.size > 0 && (
        <div style={S.bulkBar}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-main)' }}>{selected.size} selected</span>
          <span style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
            <button style={S.linkBtn} onClick={() => setSelected(new Set())}>Clear</button>
            <button style={S.bulkBtn} onClick={doBulkDecline}>Decline selected</button>
          </span>
        </div>
      )}

      {error && <p style={{ ...S.note, color: 'var(--danger)' }}>{error}</p>}

      {loading && proposals.length === 0 ? (
        <p style={S.empty}>Loading…</p>
      ) : proposals.length === 0 ? (
        <p style={S.empty}>
          No pending proposals. When the assistant or a watcher proposes something,
          it’ll appear here for you to approve.
        </p>
      ) : (
        <>
          {proposals.length > 1 && selectableCount > 0 && (
            <div style={{ ...S.filters, marginBottom: 6 }}>
              <button
                style={S.linkBtn}
                onClick={() => setSelected(new Set(proposals.filter((p) => !resolved[p.action_id]).map((p) => p.action_id)))}
              >
                Select all ({selectableCount})
              </button>
            </div>
          )}
          {proposals.map((p) => {
            const id = p.action_id;
            const st = resolved[id];
            const state = st === 'confirmed' ? 'confirmed' : st === 'rejected' ? 'rejected' : 'pending';
            const hasContext = !!p.customer_name; // inbox-context proposal (who/what)
            const meta = [];
            // "waited" off the MESSAGE's timestamp (business-hours label if the
            // backend computed one), not the proposal's created_at.
            const waited = p.waited_label || waitedFrom(p.message_received_at);
            if (waited) meta.push({ label: '', value: waited });
            if (p.rule_label) meta.push({ label: 'rule', value: p.rule_label });
            if (!hasContext && p.subject?.label) meta.push({ label: 'for', value: p.subject.label });
            if (!p.message_received_at) { const age = ageFrom(p.created_at); if (age) meta.push({ label: 'when', value: age }); }
            const subtitle = (hasContext && p.message_preview) ? (
              <>
                <span style={{ fontStyle: 'italic' }}>“{p.message_preview}”</span>
                {p.confirmation ? <span style={{ display: 'block', marginTop: 5, fontSize: 12, color: 'var(--text-muted)' }}>{p.confirmation}</span> : null}
              </>
            ) : p.confirmation;
            const badge = p.surface
              ? { text: p.shadow ? `${SURFACE_LABEL[p.surface] || p.surface} · would do` : (SURFACE_LABEL[p.surface] || p.surface), tone: SURFACE_TONE[p.surface] || 'ok' }
              : (p.shadow ? { text: 'would do', tone: 'warn' } : null);
            const isBusy = busy.has(id);
            return (
              <div style={S.row} key={id}>
                <input
                  type="checkbox"
                  style={S.check}
                  checked={selected.has(id)}
                  onChange={() => toggleSelect(id)}
                  disabled={state !== 'pending'}
                  aria-label="Select proposal"
                />
                <div style={S.cardWrap}>
                  <ConfirmCard
                    title={hasContext ? <CardTitle proposal={p} onOpenCustomer={setProfileFor} /> : toolLabel(p.tool)}
                    subtitle={subtitle}
                    meta={meta}
                    badge={badge}
                    state={state}
                    actions={[
                      {
                        label: p.shadow ? 'Confirm (shadow)' : (isBusy ? 'Confirming…' : 'Confirm'),
                        variant: 'primary',
                        onClick: () => resolveOne(p, 'confirmed'),
                        disabled: p.shadow || isBusy, // shadow only records — never executes
                      },
                      {
                        label: isBusy ? 'Declining…' : 'Decline',
                        variant: 'ghost',
                        onClick: () => resolveOne(p, 'rejected'),
                        disabled: isBusy,
                      },
                    ]}
                  />
                </div>
              </div>
            );
          })}
          {next && (
            <button style={S.loadMore} onClick={() => load(true, next)} disabled={loading}>
              {loading ? 'Loading…' : 'Load more'}
            </button>
          )}
        </>
      )}

      {profileFor ? (
        <Suspense fallback={null}>
          <CustomerPopup
            lead={{ object: 'customer', universal_id: profileFor.universal_id, name: profileFor.name }}
            leadType="customer"
            onClose={() => setProfileFor(null)}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
